/**
 * Export Utilities for Genie Chat
 * Provides client-side export functionality for PDF, HTML, and CSV formats
 */

import { jsPDF } from 'jspdf';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';
import { marked } from 'marked';
import type { ChatMessage } from '../services/genieClient';

const WELCOME_MESSAGE_PATTERN = /^👋 Hello! I'm \*\*Genie\*\*|^👋 Hello! I'm Genie/;

function formatDateForFilename(): string {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yy = String(now.getFullYear()).slice(-2);
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  const ss = String(now.getSeconds()).padStart(2, '0');
  return `${dd}-${mm}-${yy}_${hh}-${min}-${ss}`;
}

function promptToSlug(prompt: string): string {
  return prompt
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 40)
    .replace(/_$/, '');
}

function buildFilename(messages: ChatMessage[], ext: string, isFullChat: boolean): string {
  const datePart = formatDateForFilename();
  if (isFullChat) {
    return `genie_chat_${datePart}.${ext}`;
  }
  const userMsg = messages.find(m => m.role === 'user');
  const slug = userMsg ? promptToSlug(userMsg.content) : 'export';
  return `${slug}_${datePart}.${ext}`;
}

/**
 * Strip all markdown formatting to get plain text for heuristic analysis.
 */
function toPlainText(line: string): string {
  return line
    .replace(/^>\s*/, '')       // blockquote
    .replace(/^[-—–•*]+\s*/, '') // bullets
    .replace(/^\d+\.\s*/, '')   // numbered list
    .replace(/\*{1,2}([^*]+)\*{1,2}/g, '$1') // bold/italic
    .replace(/_([^_]+)_/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
    .trim();
}

function hasData(line: string): boolean {
  const plain = toPlainText(line);
  if (/^\|.*\|$/.test(line.trim())) return true;     // table row
  if (/^```/.test(line.trim())) return true;          // code fence
  if (/\d+\.\d+(e[+-]?\d+)?/i.test(plain)) return true; // scientific numbers
  return false;
}

/**
 * Patterns that indicate a "suggestion header" — a line that introduces
 * follow-up suggestions. Everything from this line to the end is stripped.
 */
const SUGGESTION_HEADER_PATTERNS = [
  /\bfollow[- ]?up questions?\b/i,
  /\bhere are (some|a few)\b.*\b(questions?|things|suggestions?)\b/i,
  /\btry (asking|these|the following)\b/i,
  /\byou (can|could|might|may) (also\s+)?(ask|try|explore)\b/i,
  /\bask\s+(me\s+)?(about|for|any)\b/i,
  /\bfeel free\b/i,
  /\blet me know\b/i,
  /\bwould you like\b/i,
  /\bif you('d| would) like\b/i,
  /\bdon'?t hesitate\b/i,
  /\bhappy to (help|assist|provide|explain|elaborate)\b/i,
  /\bi can (also\s+)?(help|provide|show|give|explain)\b/i,
  /\bshall i\b/i,
  /\bfor more (details|information|insights|info)\b/i,
  /\bwant (me\s+)?to (know|learn|explore|dive|dig)\b/i,
  /\bexplore (more|further|additional|other)\b/i,
  /\bneed (any\s+)?(more|further|additional)\b/i,
  /\bsome (things|questions) you (can|could|might)\b/i,
];

/**
 * Check if a line looks like a quoted suggestion item, e.g.:
 *   - "Show perturbation for LMNA"
 *   • "What phenotypes are shared between LMNA and TTN?"
 *   1. Compare LMNA vs TTN
 */
function isSuggestionBullet(line: string): boolean {
  const trimmed = line.trim();
  return /^[-—–•*]\s+".+"/.test(trimmed) ||
         /^[-—–•*]\s+'.+'/.test(trimmed) ||
         /^\d+\.\s+".+"/.test(trimmed) ||
         /^\d+\.\s+'.+'/.test(trimmed) ||
         /^[-—–•*]\s+\*".+"\*/.test(trimmed) ||
         /^[-—–•*]\s+\*\*".+"\*\*/.test(trimmed);
}

/**
 * Remove trailing LLM suggestion blocks from assistant content.
 *
 * Two-pass strategy:
 * 1. Top-down scan: find the LAST suggestion header line in the bottom
 *    portion of the content. If found, cut from there to the end.
 * 2. Bottom-up fallback: for single-line suggestions without a header
 *    (e.g. "Ask me for more details about X!"), strip from the bottom.
 */
function stripTrailingSuggestions(content: string): string {
  const lines = content.split('\n');

  // Trim trailing whitespace/HR lines to find real content end
  let contentEnd = lines.length;
  while (contentEnd > 0 && (!lines[contentEnd - 1].trim() || /^---+$/.test(lines[contentEnd - 1].trim()))) {
    contentEnd--;
  }
  if (contentEnd <= 1) return content;

  // --- Pass 1: top-down scan for suggestion header in the last ~30% of lines ---
  const scanStart = Math.max(1, Math.floor(contentEnd * 0.6));
  let headerIdx = -1;

  for (let i = scanStart; i < contentEnd; i++) {
    const plain = toPlainText(lines[i]);
    if (!plain) continue;
    if (hasData(lines[i].trim())) continue;

    const isHeader = SUGGESTION_HEADER_PATTERNS.some(p => p.test(plain));
    if (isHeader) {
      // Verify that everything after this line is either empty, HR,
      // more suggestion keywords, or suggestion bullets (quoted items)
      let looksLikeSuggestionBlock = true;
      for (let j = i + 1; j < contentEnd; j++) {
        const jTrimmed = lines[j].trim();
        if (!jTrimmed || /^---+$/.test(jTrimmed)) continue;
        if (isSuggestionBullet(jTrimmed)) continue;
        if (hasData(jTrimmed)) { looksLikeSuggestionBlock = false; break; }
        const jPlain = toPlainText(jTrimmed);
        const jIsKeyword = SUGGESTION_HEADER_PATTERNS.some(p => p.test(jPlain));
        // Allow short lines after a header — they're likely suggestion items
        if (jPlain.length < 200 || jIsKeyword) continue;
        looksLikeSuggestionBlock = false;
        break;
      }
      if (looksLikeSuggestionBlock) {
        headerIdx = i;
        break; // take the first (earliest) suggestion header we find
      }
    }
  }

  if (headerIdx > 0) {
    // Also strip any preceding empty lines / HR above the header
    let cutPoint = headerIdx;
    while (cutPoint > 0 && (!lines[cutPoint - 1].trim() || /^---+$/.test(lines[cutPoint - 1].trim()))) {
      cutPoint--;
    }
    if (cutPoint > 0) {
      return lines.slice(0, cutPoint).join('\n').trimEnd();
    }
  }

  // --- Pass 2: bottom-up fallback for standalone suggestion lines ---
  let end = contentEnd;
  while (end > 1) {
    const trimmed = lines[end - 1].trim();
    if (!trimmed || /^---+$/.test(trimmed)) { end--; continue; }
    if (hasData(trimmed)) break;

    const plain = toPlainText(trimmed);
    if (!plain) { end--; continue; }

    const keywordMatch = SUGGESTION_HEADER_PATTERNS.some(p => p.test(plain));
    if (keywordMatch && plain.length < 300) {
      end--;
      continue;
    }

    break;
  }

  // Clean trailing empty/HR above
  while (end > 0 && (!lines[end - 1].trim() || /^---+$/.test(lines[end - 1].trim()))) {
    end--;
  }

  if (end >= lines.length || end <= 0) return content;
  return lines.slice(0, end).join('\n').trimEnd();
}

/**
 * Prepare messages for export: filter welcome, strip trailing suggestions.
 */
function prepareForExport(messages: ChatMessage[], skipWelcome: boolean): ChatMessage[] {
  const filtered = skipWelcome ? filterWelcomeMessage(messages) : messages;
  return filtered.map(msg => {
    if (msg.role === 'assistant') {
      return { ...msg, content: stripTrailingSuggestions(msg.content) };
    }
    return msg;
  });
}

export function filterWelcomeMessage(messages: ChatMessage[]): ChatMessage[] {
  return messages.filter(msg => {
    if (msg.role === 'assistant' && WELCOME_MESSAGE_PATTERN.test(msg.content)) {
      return false;
    }
    return true;
  });
}

export function isWelcomeMessage(content: string): boolean {
  return WELCOME_MESSAGE_PATTERN.test(content);
}

// ─── Markdown Table Extraction ───────────────────────────────────────────────

function extractTablesFromMarkdown(markdown: string): Array<{ headers: string[]; rows: string[][] }> {
  const tables: Array<{ headers: string[]; rows: string[][] }> = [];
  const lines = markdown.split('\n');

  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();

    if (line.startsWith('|') && line.endsWith('|')) {
      const sepLine = i + 1 < lines.length ? lines[i + 1].trim() : '';
      if (/^\|[\s:]*-+[\s:]*(\|[\s:]*-+[\s:]*)*\|$/.test(sepLine)) {
        const parseCells = (l: string) =>
          l.split('|').slice(1, -1).map(c => c.trim());

        const headers = parseCells(line);
        const rows: string[][] = [];
        let j = i + 2;
        while (j < lines.length) {
          const rowLine = lines[j].trim();
          if (rowLine.startsWith('|') && rowLine.endsWith('|')) {
            rows.push(parseCells(rowLine));
            j++;
          } else {
            break;
          }
        }
        if (headers.length > 0 && rows.length > 0) {
          tables.push({ headers, rows });
        }
        i = j;
        continue;
      }
    }
    i++;
  }
  return tables;
}

function stripMarkdownFormatting(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/\[(.+?)\]\(.+?\)/g, '$1');
}

/**
 * Strip emojis and non-ASCII symbols that jsPDF's built-in Helvetica can't render.
 * Without this, a single emoji corrupts the entire surrounding text run.
 */
function stripEmoji(text: string): string {
  return text
    // eslint-disable-next-line no-control-regex
    .replace(/[\u{1F000}-\u{1FFFF}]/gu, '')
    .replace(/[\u{2600}-\u{27BF}]/gu, '')
    .replace(/[\u{FE00}-\u{FE0F}]/gu, '')
    .replace(/[\u{200D}]/gu, '')
    .replace(/[\u{E0020}-\u{E007F}]/gu, '')
    .trim();
}

function safePdfText(text: string): string {
  return stripEmoji(stripMarkdownFormatting(text));
}

function isTableLine(line: string): boolean {
  const t = line.trim();
  return t.startsWith('|') && t.endsWith('|');
}

function isSeparatorLine(line: string): boolean {
  return /^\|[\s:]*-+[\s:]*(\|[\s:]*-+[\s:]*)*\|$/.test(line.trim());
}

// ─── PDF Export ──────────────────────────────────────────────────────────────

export async function exportChatAsPdf(
  _messagesContainerId: string,
  filename?: string,
  skipWelcome: boolean = true,
  messages?: ChatMessage[]
): Promise<void> {
  if (!messages || messages.length === 0) {
    throw new Error('No messages to export');
  }

  const filteredMessages = prepareForExport(messages, skipWelcome);
  if (filteredMessages.length === 0) {
    throw new Error('No messages to export');
  }

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = 210;
  const pageH = 297;
  const marginL = 15;
  const marginR = 15;
  const marginTop = 20;
  const marginBottom = 15;
  const usableW = pageW - marginL - marginR;
  let y = marginTop;

  function checkPage(needed: number) {
    if (y + needed > pageH - marginBottom) {
      pdf.addPage();
      y = marginTop;
    }
  }

  // Header
  pdf.setFillColor(102, 126, 234);
  pdf.rect(0, 0, pageW, 30, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Genie Chat Export', marginL, 15);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Exported: ${new Date().toLocaleString()}`, marginL, 23);
  y = 38;

  for (const msg of filteredMessages) {
    const isUser = msg.role === 'user';
    const label = isUser ? 'User:' : 'Genie:';

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(isUser ? 102 : 80, isUser ? 126 : 80, isUser ? 234 : 80);
    checkPage(8);
    pdf.text(label.toUpperCase(), marginL, y);
    y += 5;

    if (isUser) {
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(255, 255, 255);
      const plain = safePdfText(msg.content);
      const lines = pdf.splitTextToSize(plain, usableW - 20);
      const blockH = lines.length * 5 + 6;
      checkPage(blockH);
      pdf.setFillColor(102, 126, 234);
      pdf.roundedRect(marginL, y - 3, usableW, blockH, 3, 3, 'F');
      pdf.text(lines, marginL + 5, y + 2);
      y += blockH + 4;
    } else {
      pdf.setTextColor(50, 50, 50);
      const allLines = msg.content.split('\n');

      // Helper to render a table inline at current position
      const renderPdfTable = (startLine: number): number => {
        const parseCells = (l: string) =>
          l.split('|').slice(1, -1).map(c => c.trim());

        const headers = parseCells(allLines[startLine].trim());
        const rows: string[][] = [];
        let j = startLine + 2; // skip header + separator
        while (j < allLines.length) {
          const rowLine = allLines[j].trim();
          if (rowLine.startsWith('|') && rowLine.endsWith('|')) {
            rows.push(parseCells(rowLine));
            j++;
          } else {
            break;
          }
        }

        if (headers.length > 0 && rows.length > 0) {
          // Filter out columns that are purely emoji/non-printable
          const validCols = headers.map((_, ci) => {
            const colValues = rows.map(r => r[ci] || '');
            return colValues.some(v => safePdfText(v).length > 0);
          });
          const filteredHeaders = headers.filter((_, ci) => validCols[ci]);
          const filteredRows = rows.map(r => r.filter((_, ci) => validCols[ci]));

          const colCount = filteredHeaders.length;
          if (colCount > 0) {
            const colW = Math.min((usableW - 2) / colCount, 45);
            const tableW = colW * colCount;
            const rowH = 7;
            const headerH = 8;

            checkPage(headerH + rowH * Math.min(filteredRows.length, 3) + 4);

            pdf.setFillColor(74, 85, 104);
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(8);
            pdf.setFont('helvetica', 'bold');
            pdf.rect(marginL, y, tableW, headerH, 'F');
            filteredHeaders.forEach((h, ci) => {
              const cellText = pdf.splitTextToSize(safePdfText(h), colW - 3);
              pdf.text(cellText[0] || '', marginL + ci * colW + 2, y + 5.5);
            });
            y += headerH;

            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(50, 50, 50);
            filteredRows.forEach((row, ri) => {
              checkPage(rowH + 2);
              if (ri % 2 === 0) {
                pdf.setFillColor(247, 250, 252);
                pdf.rect(marginL, y, tableW, rowH, 'F');
              }
              pdf.setDrawColor(200, 200, 200);
              pdf.rect(marginL, y, tableW, rowH, 'S');

              row.forEach((cell, ci) => {
                if (ci >= colCount) return;
                const cellText = pdf.splitTextToSize(safePdfText(cell), colW - 3);
                pdf.text(cellText[0] || '', marginL + ci * colW + 2, y + 5);
              });
              y += rowH;
            });
            y += 5;
          }
        }

        return j; // return next line index after the table
      };

      let lineIdx = 0;
      while (lineIdx < allLines.length) {
        const trimmed = allLines[lineIdx].trim();

        if (!trimmed) { lineIdx++; continue; }

        // Table — render inline at this position
        if (isTableLine(trimmed)) {
          const nextLine = lineIdx + 1 < allLines.length ? allLines[lineIdx + 1].trim() : '';
          if (isSeparatorLine(nextLine)) {
            lineIdx = renderPdfTable(lineIdx);
            continue;
          }
          if (isSeparatorLine(trimmed)) { lineIdx++; continue; }
        }

        // Heading
        const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)/);
        if (headingMatch) {
          const level = headingMatch[1].length;
          const text = safePdfText(headingMatch[2]);
          pdf.setFontSize(level === 1 ? 14 : level === 2 ? 12 : 11);
          pdf.setFont('helvetica', 'bold');
          checkPage(8);
          pdf.text(text, marginL, y);
          y += level === 1 ? 8 : 7;
          pdf.setFont('helvetica', 'normal');
          lineIdx++;
          continue;
        }

        // Horizontal rule
        if (/^---+$/.test(trimmed)) {
          pdf.setDrawColor(200, 200, 200);
          checkPage(4);
          pdf.line(marginL, y, pageW - marginR, y);
          y += 5;
          lineIdx++;
          continue;
        }

        // Bullet or numbered list item
        const listMatch = trimmed.match(/^(\s*[-*]\s+|\s*\d+\.\s+)(.+)/);
        if (listMatch) {
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          const cleaned = safePdfText(listMatch[2]);
          const bullet = /^\s*\d+\./.test(listMatch[1])
            ? listMatch[1].trim()
            : '-';
          const wrappedLines = pdf.splitTextToSize(cleaned, usableW - 12);
          checkPage(wrappedLines.length * 5 + 2);
          pdf.text(bullet, marginL + 2, y);
          pdf.text(wrappedLines, marginL + 10, y);
          y += wrappedLines.length * 5 + 1;
          lineIdx++;
          continue;
        }

        // Plain text paragraph
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        let paraText = '';
        while (lineIdx < allLines.length) {
          const pLine = allLines[lineIdx].trim();
          if (!pLine) break;
          if (/^#{1,3}\s/.test(pLine)) break;
          if (/^---+$/.test(pLine)) break;
          if (/^\s*[-*]\s+/.test(pLine) || /^\s*\d+\.\s+/.test(pLine)) break;
          if (isTableLine(pLine)) break;
          paraText += (paraText ? ' ' : '') + pLine;
          lineIdx++;
        }
        if (paraText) {
          const cleanText = safePdfText(paraText);
          const wrappedLines = pdf.splitTextToSize(cleanText, usableW);
          checkPage(wrappedLines.length * 5 + 2);
          pdf.text(wrappedLines, marginL, y);
          y += wrappedLines.length * 5 + 3;
        }
        continue;
      }
      y += 3;
    }

    // Divider between messages
    pdf.setDrawColor(220, 220, 220);
    checkPage(4);
    pdf.line(marginL, y, pageW - marginR, y);
    y += 6;
  }

  const defaultFilename = buildFilename(filteredMessages, 'pdf', true);
  pdf.save(filename || defaultFilename);
}

// ─── HTML Export ─────────────────────────────────────────────────────────────

export function exportChatAsHtml(
  messages: ChatMessage[],
  filename?: string,
  skipWelcome: boolean = true
): void {
  const filteredMessages = prepareForExport(messages, skipWelcome);
  if (filteredMessages.length === 0) {
    throw new Error('No messages to export');
  }

  const timestamp = new Date().toLocaleString();

  const renderedMessages = filteredMessages.map(msg => {
    const renderedContent = msg.role === 'assistant'
      ? marked.parse(msg.content, { gfm: true, breaks: true }) as string
      : `<p>${escapeHtml(msg.content)}</p>`;

    return `
        <div class="message ${msg.role}">
            <div class="message-role">${msg.role === 'user' ? 'User:' : 'Genie:'}</div>
            <div class="message-content">${renderedContent}</div>
        </div>`;
  }).join('\n');

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Genie Chat Export</title>
    <style>
        * { box-sizing: border-box; }
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            max-width: 960px;
            margin: 0 auto;
            padding: 20px;
            background-color: #f5f5f5;
            line-height: 1.6;
            color: #333;
        }
        .header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 30px;
            border-radius: 10px;
            margin-bottom: 30px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        .header h1 { margin: 0 0 10px 0; font-size: 28px; }
        .header .subtitle { opacity: 0.9; font-size: 14px; margin: 4px 0; }
        .message {
            margin-bottom: 20px;
            padding: 18px 22px;
            border-radius: 12px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
        }
        .message.user {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            margin-left: 80px;
            border-bottom-right-radius: 4px;
        }
        .message.user .message-content p { color: white; }
        .message.assistant {
            background: white;
            color: #333;
            margin-right: 40px;
            border-bottom-left-radius: 4px;
            border: 1px solid #e2e8f0;
        }
        .message-role {
            font-weight: 700;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 10px;
            opacity: 0.75;
        }
        .message-content { font-size: 14px; word-wrap: break-word; }
        .message-content p { margin: 6px 0; }
        .message-content h1 { font-size: 20px; margin: 16px 0 8px; color: #1a365d; }
        .message-content h2 { font-size: 17px; margin: 14px 0 6px; color: #2d3748; }
        .message-content h3 { font-size: 15px; margin: 12px 0 4px; color: #4a5568; }
        .message-content ul, .message-content ol {
            margin: 8px 0; padding-left: 24px;
        }
        .message-content li { margin: 4px 0; font-size: 14px; }
        .message-content strong { color: #2c5282; font-weight: 700; }
        .message.user .message-content strong { color: #e0e7ff; }
        .message-content em { font-style: italic; }
        .message-content code {
            background-color: #edf2f7;
            padding: 2px 6px;
            border-radius: 4px;
            font-family: 'Consolas', 'Monaco', monospace;
            font-size: 13px;
            color: #c53030;
        }
        .message-content pre {
            background-color: #1a202c;
            color: #e2e8f0;
            padding: 14px;
            border-radius: 8px;
            overflow-x: auto;
            font-size: 13px;
            line-height: 1.5;
        }
        .message-content pre code {
            background: none;
            color: inherit;
            padding: 0;
        }
        .message-content blockquote {
            border-left: 4px solid #667eea;
            padding-left: 14px;
            margin: 10px 0;
            color: #4a5568;
            font-style: italic;
        }
        .message-content hr {
            border: none;
            border-top: 2px solid #e2e8f0;
            margin: 16px 0;
        }

        /* Table styling */
        .message-content table {
            width: 100%;
            border-collapse: collapse;
            margin: 14px 0;
            font-size: 13px;
            border: 1px solid #cbd5e0;
            border-radius: 6px;
            overflow: hidden;
        }
        .message-content thead { background-color: #4a5568; }
        .message-content th {
            padding: 10px 14px;
            text-align: left;
            font-weight: 600;
            color: white;
            border: 1px solid #4a5568;
            white-space: nowrap;
        }
        .message-content td {
            padding: 9px 14px;
            text-align: left;
            border: 1px solid #e2e8f0;
            color: #2d3748;
        }
        .message-content tbody tr:nth-child(even) { background-color: #f7fafc; }
        .message-content tbody tr:hover { background-color: #edf2f7; }

        .footer {
            text-align: center;
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #e2e8f0;
            color: #718096;
            font-size: 12px;
        }
        @media print {
            body { background: white; }
            .message { break-inside: avoid; }
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>&#x1F9EC; Genie Chat Export</h1>
        <p class="subtitle">TargetX AI Assistant Conversation</p>
        <p class="subtitle">Exported: ${timestamp}</p>
    </div>

    <div class="messages">
        ${renderedMessages}
    </div>

    <div class="footer">
        <p>Generated by TargetX Genie AI Assistant</p>
        <p>This export is for research purposes only.</p>
    </div>
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const defaultFilename = buildFilename(filteredMessages, 'html', true);
  saveAs(blob, filename || defaultFilename);
}

// ─── CSV Export ──────────────────────────────────────────────────────────────

/**
 * Check if a string contains only emoji / non-ASCII symbols with no meaningful text.
 */
function isEmojiOnly(text: string): boolean {
  const stripped = text
    .replace(/[\u{1F000}-\u{1FFFF}]/gu, '')
    .replace(/[\u{2600}-\u{27BF}]/gu, '')
    .replace(/[\u{FE00}-\u{FE0F}]/gu, '')
    .replace(/[\u{200D}]/gu, '')
    .replace(/[\u{E0020}-\u{E007F}]/gu, '')
    .replace(/[\u{2700}-\u{27BF}]/gu, '')
    .replace(/[\u{2300}-\u{23FF}]/gu, '')
    .replace(/[\u{2B50}\u{2B05}-\u{2B07}\u{2934}\u{2935}]/gu, '')
    .replace(/[\u{25A0}-\u{25FF}]/gu, '')
    .replace(/[\u{2600}-\u{26FF}]/gu, '')
    .trim();
  return stripped.length === 0;
}

/**
 * Filter out columns from a table where every data cell is purely emoji/icon.
 */
function filterEmojiColumns(
  headers: string[],
  rows: string[][]
): { headers: string[]; rows: string[][] } {
  const validCols = headers.map((_, ci) => {
    const allCells = rows.map(r => (r[ci] || '').trim());
    return allCells.some(cell => cell.length > 0 && !isEmojiOnly(cell));
  });

  return {
    headers: headers.filter((_, ci) => validCols[ci]),
    rows: rows.map(r => r.filter((_, ci) => validCols[ci])),
  };
}

/**
 * Build CSV blocks from messages, pairing each assistant's tables with the
 * preceding user prompt so context is preserved.
 */
function buildCsvFromMessages(
  messages: ChatMessage[]
): string {
  let csvContent = '';
  let tableCount = 0;

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    if (msg.role !== 'assistant') continue;

    const tables = extractTablesFromMarkdown(msg.content);
    if (tables.length === 0) continue;

    const userPrompt = i > 0 && messages[i - 1]?.role === 'user'
      ? messages[i - 1].content
      : undefined;

    for (const table of tables) {
      const filtered = filterEmojiColumns(table.headers, table.rows);
      if (filtered.headers.length === 0) continue;

      if (tableCount > 0) {
        csvContent += '\r\n\r\n';
      }

      if (userPrompt) {
        csvContent += `"User: ${userPrompt.replace(/"/g, '""')}"\r\n`;
      }

      const allRows = [filtered.headers, ...filtered.rows];
      const csvBlock = Papa.unparse(allRows, {
        quotes: true,
        quoteChar: '"',
        escapeChar: '"',
        delimiter: ',',
        newline: '\r\n',
        header: false,
      });
      csvContent += csvBlock;
      tableCount++;
    }
  }

  return csvContent;
}

export function exportTablesAsCsv(
  messages: ChatMessage[],
  filename?: string,
  skipWelcome: boolean = true
): void {
  const filteredMessages = prepareForExport(messages, skipWelcome);

  const csvContent = buildCsvFromMessages(filteredMessages);
  if (!csvContent) {
    throw new Error('No tables found in the conversation to export');
  }

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
  const defaultFilename = buildFilename(filteredMessages, 'csv', true);
  saveAs(blob, filename || defaultFilename);
}

// ─── Single Message Exports ──────────────────────────────────────────────────

/**
 * Export a user+assistant message pair as PDF.
 * Accepts a ChatMessage[] containing the user prompt and assistant response.
 */
/**
 * Export a user+assistant message pair as HTML.
 */
export function exportMessageAsHtml(
  messagePair: ChatMessage[],
  filename?: string
): void {
  const defaultName = buildFilename(messagePair, 'html', false);
  exportChatAsHtml(messagePair, filename || defaultName, false);
}

export async function exportMessageAsPdf(
  messagePair: ChatMessage[],
  _title: string = 'Genie Report',
  filename?: string
): Promise<void> {
  const defaultName = buildFilename(messagePair, 'pdf', false);
  await exportChatAsPdf('', filename || defaultName, false, messagePair);
}

/**
 * Export tables from a user+assistant message pair as CSV.
 * Accepts a ChatMessage[] containing the user prompt and assistant response.
 */
export function exportMessageTablesAsCsv(
  messagePair: ChatMessage[],
  filename?: string
): void {
  const prepared = prepareForExport(messagePair, false);
  const csvContent = buildCsvFromMessages(prepared);
  if (!csvContent) {
    throw new Error('No tables found in this message to export');
  }

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
  const defaultFilename = buildFilename(messagePair, 'csv', false);
  saveAs(blob, filename || defaultFilename);
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
